import { useState } from 'react';
import { X, Search, Loader2, CheckSquare, Square, Download, Printer, FileText, LayoutGrid } from 'lucide-react';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { useAuthStore } from '@/store/authStore';
import { useBulkItemSelection } from '@/hooks/useBulkItemSelection';
import { exportCatalogPdf, printCatalogHtml, type CatalogExportLayout } from '@/utils/catalogExportPdf';

interface Props {
  onClose: () => void;
}

export default function CatalogExportModal({ onClose }: Props) {
  const { t } = useTranslation();
  const company = useAuthStore((s) => s.company);
  const sel = useBulkItemSelection('Product');
  const [layout, setLayout] = useState<CatalogExportLayout>('brochure');
  const [includeWholesale, setIncludeWholesale] = useState(false);
  const [exporting, setExporting] = useState(false);

  const rowsForExport = sel.selectedRows.length > 0 ? sel.selectedRows : sel.flatRows;

  const handleExport = async () => {
    if (rowsForExport.length === 0) {
      toast.error(t('items.noProductsSelected'));
      return;
    }
    setExporting(true);
    try {
      await exportCatalogPdf(rowsForExport, {
        companyName: company?.name || 'Store',
        layout,
        includeWholesale,
        includeBarcode: layout === 'priceSheet',
      });
      toast.success(t('items.catalogExported'));
    } catch (err) {
      toast.error((err as Error)?.message || t('items.catalogExportFail'));
    } finally {
      setExporting(false);
    }
  };

  const handlePrint = () => {
    if (rowsForExport.length === 0) {
      toast.error(t('items.noProductsSelected'));
      return;
    }
    printCatalogHtml(rowsForExport, {
      companyName: company?.name || 'Store',
      layout,
      includeWholesale,
      includeBarcode: layout === 'priceSheet',
    });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-3xl max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <div>
            <h2 className="text-lg font-bold text-gray-900">{t('items.exportCatalog')}</h2>
            <p className="text-sm text-gray-500">{t('items.exportCatalogHelp')}</p>
          </div>
          <button type="button" onClick={onClose} className="p-2 rounded-lg hover:bg-gray-100">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-4 space-y-4 border-b border-gray-100">
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() => setLayout('brochure')}
              className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg border text-sm font-medium ${layout === 'brochure' ? 'border-primary-500 bg-primary-50 text-primary-800' : 'border-gray-200'}`}
            >
              <LayoutGrid className="w-4 h-4" /> {t('items.brochureLayout')}
            </button>
            <button
              type="button"
              onClick={() => setLayout('priceSheet')}
              className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg border text-sm font-medium ${layout === 'priceSheet' ? 'border-primary-500 bg-primary-50 text-primary-800' : 'border-gray-200'}`}
            >
              <FileText className="w-4 h-4" /> {t('items.priceSheetLayout')}
            </button>
          </div>

          <div className="flex flex-wrap gap-3 items-center">
            <div className="relative flex-1 min-w-[200px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                className="input-field pl-9 text-sm"
                placeholder={t('common.search')}
                value={sel.search}
                onChange={(e) => sel.setSearch(e.target.value)}
              />
            </div>
            <select className="input-field text-sm w-40" value={sel.category} onChange={(e) => sel.setCategory(e.target.value)}>
              <option value="All">{t('items.allCategories')}</option>
              {sel.categories.map((c) => (
                <option key={c.name} value={c.name}>{c.category_name}</option>
              ))}
            </select>
          </div>

          <div className="flex flex-wrap gap-2 items-center">
            <button type="button" onClick={() => void sel.selectAllVisible()} className="btn-secondary text-xs py-2">{t('items.selectAll')}</button>
            <button type="button" onClick={sel.clearSelection} className="btn-secondary text-xs py-2">{t('items.clearSelection')}</button>
            {layout === 'priceSheet' && (
              <label className="inline-flex items-center gap-2 text-sm text-gray-600 ml-2">
                <input type="checkbox" checked={includeWholesale} onChange={(e) => setIncludeWholesale(e.target.checked)} />
                {t('items.includeWholesale')}
              </label>
            )}
            <span className="text-xs text-gray-500 ml-auto">
              {t('items.exportCount', { count: rowsForExport.length })}
            </span>
          </div>
        </div>

        <div className="flex-1 overflow-auto min-h-[200px]">
          {sel.isLoading ? (
            <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-gray-400" /></div>
          ) : (
            sel.flatRows.map((row) => {
              const checked = sel.selectedKeys.has(row.key);
              return (
                <button
                  key={row.key}
                  type="button"
                  onClick={() => sel.toggleKey(row.key)}
                  className={`w-full flex items-center gap-3 px-4 py-2 border-b border-gray-50 text-left hover:bg-gray-50 ${checked ? 'bg-primary-50/30' : ''}`}
                >
                  {checked ? <CheckSquare className="w-4 h-4 text-primary-600" /> : <Square className="w-4 h-4 text-gray-400" />}
                  <span className="text-sm text-gray-900 flex-1 truncate">{row.label}</span>
                  <span className="text-xs text-gray-500">{row.categoryName}</span>
                </button>
              );
            })
          )}
        </div>

        <div className="p-4 border-t border-gray-100 flex justify-end gap-2">
          <button type="button" onClick={handlePrint} disabled={exporting} className="btn-secondary flex items-center gap-2">
            <Printer className="w-4 h-4" /> {t('common.print')}
          </button>
          <button type="button" onClick={() => void handleExport()} disabled={exporting} className="btn-primary flex items-center gap-2">
            {exporting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
            {t('items.downloadPdf')}
          </button>
        </div>
      </div>
    </div>
  );
}
